// Doc section 8 "Section visibility": IntersectionObserver over a small,
// deliberately curated list of ecommerce sections — never every DOM
// element. Sections are identified by a `data-hh-section="..."` attribute,
// which the Theme App Extension's embed block instructs the merchant/theme
// to add to the handful of sections that matter (product hero, variant
// selector, reviews, bundle, shipping info, etc. — doc's own list).

import { KNOWN_SECTION_CONTAINERS } from "./themeSelectors.js";

const pageLoadedAt = performance.now();

export function trackSections(onSectionView: (sectionKey: string, timeToFirstViewMs: number) => void): { stop: () => void } {
  const seen = new Set<string>();
  const targetsByKey = new Map<string, HTMLElement>();

  // Explicit data-hh-section markers (if a theme dev added them) take
  // priority; known theme-verified containers fill in the rest so this
  // works without any theme edits.
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-hh-section]"))) {
    const key = el.getAttribute("data-hh-section");
    if (key) targetsByKey.set(key, el);
  }
  for (const known of KNOWN_SECTION_CONTAINERS) {
    if (targetsByKey.has(known.key)) continue;
    const el = document.querySelector<HTMLElement>(known.selector);
    if (el) targetsByKey.set(known.key, el);
  }

  if (targetsByKey.size === 0 || typeof IntersectionObserver === "undefined") {
    return { stop: () => {} };
  }

  const keyByElement = new Map<Element, string>();
  for (const [key, el] of targetsByKey) keyByElement.set(el, key);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const key = keyByElement.get(entry.target);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        onSectionView(key, Math.round(performance.now() - pageLoadedAt));
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.4 } // section counted "viewed" once 40% visible
  );

  for (const el of targetsByKey.values()) observer.observe(el);

  return { stop: () => observer.disconnect() };
}
